import React, { forwardRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    Divider
} from '@mui/material';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const WeeklywisePrint = forwardRef(({ 
    processedTasks = [], 
    propertyName = '', 
    shiftName = '',
    searchCriteria = '',
    title = 'Weekly Task Allocation Report'
}, ref) => {
    
    console.log('Processed Tasks for Printing:', processedTasks)
    const groupedByYojana = processedTasks.reduce((acc, task) => {
        const yojanaKey = task.yojanaName || 'Uncategorized';
        if (!acc[yojanaKey]) {
            acc[yojanaKey] = {
                yojanaName: yojanaKey,
                areas: {}
            };
        }
        const areaKey = `${yojanaKey}_${task.areaName}`;
        if (!acc[yojanaKey].areas[areaKey]) {
            acc[yojanaKey].areas[areaKey] = {
                areaName: task.areaName,
                tasks: []
            };
        }
        
        acc[yojanaKey].areas[areaKey].tasks.push(task);
        return acc;
    }, {});

    const yojanaGroups = Object.values(groupedByYojana);

    const activeDays = DAYS.filter(day => 
        processedTasks.some(task => task.days[day])
    );

    const daysToShow = activeDays.length > 0 ? activeDays : DAYS;

    const YojanaTable = ({ yojanaGroup, yojanaIndex }) => {
        const areaGroups = Object.values(yojanaGroup.areas);
        
        return (
            <Box key={yojanaIndex} sx={{ 
                pageBreakAfter: 'always',
                marginBottom: '20px',
                '@media print': {
                    pageBreakAfter: 'always',
                    pageBreakInside: 'avoid'
                }
            }}>
               
                <Box sx={{ 
                    textAlign: 'center', 
                    marginBottom: '6px',
                    paddingBottom: '4px'
                }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginBottom: '2px',
                        lineHeight: 1.2
                    }}>
                        {yojanaGroup.yojanaName || title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontSize: '12px',
                        lineHeight: 1.1
                    }}>
                        <strong>Property:</strong> {propertyName} || <strong>Shift:</strong> {shiftName}
                    </Typography>
                </Box>

                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontSize: '13px',
                    fontWeight: 'bold'
                }}>
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                        <strong>Staff Name:</strong> ______________
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                        <strong>Issue Date:</strong> ________
                    </Typography>
                </Box>

                {areaGroups.length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        padding: '20px',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        <Typography>No tasks to print</Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table 
                            size="small" 
                            sx={{ 
                                width: '100%',
                                borderCollapse: 'collapse',
                                '& .MuiTableCell-root': {
                                    border: '1px solid #000',
                                    padding: '2px 3px',
                                    fontSize: '8px',
                                    lineHeight: 1
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow sx={{ 
                                    backgroundColor: '#f0f0f0',
                                    '& th': {
                                        padding: '1px 2px !important'
                                    }
                                }}>
                                    <TableCell 
                                        rowSpan={2}
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            width: '80px',
                                            minWidth: '80px',
                                            maxWidth: '80px',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Area
                                    </TableCell>
                                    <TableCell 
                                        rowSpan={2}
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            width: '150px',
                                            minWidth: '150px',
                                            maxWidth: '150px',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Task
                                    </TableCell>
                                    {daysToShow.map(day => (
                                        <TableCell 
                                            key={`day-${day}`}
                                            align="center"
                                            colSpan={2}
                                            sx={{ 
                                                fontWeight: 'bold',
                                                padding: '1px 1px !important',
                                                minWidth: '20px',
                                                maxWidth: '20px'
                                            }}
                                        >
                                            {day}
                                        </TableCell>
                                    ))}
                                </TableRow>

                                <TableRow sx={{ 
                                    backgroundColor: '#f5f5f5',
                                    '& td': {
                                        padding: '1px !important',
                                        borderTop: 'none'
                                    }
                                }}>
                                    {daysToShow.map((day, idx) => (
                                        <React.Fragment key={`day-labels-${idx}`}>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    fontSize: '7px',
                                                    borderRight: 'none',
                                                    padding: '0px 1px !important'
                                                }}
                                            >
                                                A
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                sx={{ 
                                                    fontSize: '7px',
                                                    borderLeft: 'none',
                                                    padding: '0px 1px !important'
                                                }}
                                            >
                                                M
                                            </TableCell>
                                        </React.Fragment>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {areaGroups.map((areaGroup, areaIndex) => (
                                    <React.Fragment key={areaIndex}>
                                        {areaGroup.tasks.map((task, taskIndex) => (
                                            <TableRow key={`${areaIndex}-${taskIndex}`}>
                                                {taskIndex === 0 && (
                                                    <TableCell 
                                                        rowSpan={areaGroup.tasks.length}
                                                        sx={{ 
                                                            fontWeight: 'bold',
                                                            backgroundColor: '#f9f9f9',
                                                            verticalAlign: 'top',
                                                            padding: '3px 4px !important',
                                                            width: '80px',
                                                            minWidth: '80px',
                                                            maxWidth: '80px',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        {areaGroup.areaName}
                                                    </TableCell>
                                                )}
                                                <TableCell sx={{ 
                                                    fontWeight: 'bold',
                                                    padding: '3px 4px !important',
                                                    width: '150px',
                                                    minWidth: '150px',
                                                    maxWidth: '150px',
                                                    fontSize: '14px'
                                                }}>
                                                    {task.taskName}
                                                </TableCell>
                                                
                                                {daysToShow.map(day => (
                                                    <React.Fragment key={`checkboxes-${day}-${taskIndex}`}>
                                                        <TableCell 
                                                            align="center" 
                                                            sx={{ 
                                                                padding: '1px !important',
                                                                borderRight: 'none'
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: '10px',
                                                                height: '10px',
                                                                border: '1px solid #000',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '7px',
                                                                fontWeight: 'bold',
                                                                margin: 'auto'
                                                            }}>
                                                                {task.days[day] ? '✓' : ''}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell 
                                                            align="center" 
                                                            sx={{ 
                                                                padding: '1px !important',
                                                                borderLeft: 'none'
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: '10px',
                                                                height: '10px',
                                                                border: '1px solid #000',
                                                                display: 'inline-flex',
                                                                margin: 'auto'
                                                            }} />
                                                        </TableCell>
                                                    </React.Fragment>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}

                                <TableRow sx={{ 
                                    backgroundColor: '#f9f9f9',
                                    '& td': {
                                        padding: '1px 2px !important',
                                        fontSize: '8px'
                                    }
                                }}>
                                    <TableCell 
                                        colSpan={2}
                                        sx={{ 
                                            fontWeight: 'bold',
                                            textAlign: 'center'
                                        }}
                                    >
                                        Auditor Sign
                                    </TableCell>
                                    {daysToShow.map((day, idx) => (
                                        <React.Fragment key={`audit-${idx}`}>
                                            <TableCell 
                                                align="center" 
                                                sx={{ padding: '1px !important' }}
                                            >
                                                <Box sx={{ height: '12px' }} />
                                            </TableCell>
                                            <TableCell 
                                                align="center" 
                                                sx={{ padding: '1px !important' }}
                                            >
                                                <Box sx={{ height: '12px' }} />
                                            </TableCell>
                                        </React.Fragment>
                                    ))}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box sx={{ 
                    marginTop: '8px',
                    paddingTop: '4px',
                    borderTop: '1px solid #ccc',
                    fontSize: '8px',
                    color: '#666',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Typography variant="caption" sx={{ fontSize: '8px' }}>
                        Printed: {new Date().toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '8px' }}>
                        Page: {yojanaIndex + 1} of {yojanaGroups.length} | 
                        Tasks: {areaGroups.reduce((sum, area) => sum + area.tasks.length, 0)} | 
                        Areas: {areaGroups.length}
                    </Typography>
                </Box>
                {yojanaIndex < yojanaGroups.length - 1 && (
                    <Box sx={{ 
                        height: '20px',
                        '@media print': {
                            height: '0px'
                        }
                    }}>
                        <Divider sx={{ marginY: '10px' }} />
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box ref={ref} sx={{ 
            padding: '0px',
            backgroundColor: 'white',
            '@media print': {
                padding: '0px',
                '@page': {
                    size: 'A4 ',
                    margin: '3mm'
                }
            }
        }}>
            <Box sx={{ 
                padding: '5mm',
                minHeight: 'auto',
                width: '100%'
            }}>
                {yojanaGroups.length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        padding: '20px',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        <Typography>No tasks to print</Typography>
                    </Box>
                ) : (
                    <>
                        
                        {yojanaGroups.map((yojanaGroup, index) => (
                            <YojanaTable 
                                key={index} 
                                yojanaGroup={yojanaGroup} 
                                yojanaIndex={index} 
                            />
                        ))}
                    </>
                )}
            </Box>
        </Box>
    );
});

WeeklywisePrint.displayName = 'WeeklywisePrint';

export default WeeklywisePrint;